import { useState } from 'react'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import * as enrollmentActions from '../../../actions/enrollmentActions'
import { invoiceRefundAmount } from '../../../utility/enrollmentsBilling'

// Post-drop success title, reflecting whether/how much was refunded.
const dropSuccessTitleFor = (enrollment, refundAmount, t) => {
  const paid = Number(enrollment?.invoice?.total ?? enrollment?.transactionAmount ?? enrollment?.amount ?? 0)
  const remaining = Math.max(paid - invoiceRefundAmount(enrollment?.invoice), 0)
  const amount = Number(refundAmount || 0)

  if (amount <= 0) { return t('enrollmentsBilling.actions.enrollmentDropped') }
  return amount >= remaining
    ? t('enrollmentsBilling.actions.enrollmentDroppedRefunded')
    : t('enrollmentsBilling.actions.enrollmentDroppedPartiallyRefunded')
}

// Enrollment drop actions (plain Drop + Drop/Refund), and the post-drop success modal title.
// Extracted from EnrollmentDetailDrawer to keep that component under the max-lines limit.
const useDropActions = ({
  listEnrollment, enrollment, coachMessage, notifyViaEmail, setActionLoading, closeModal, onActionComplete, loadEnrollment,
  refundOnly,
}) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const [dropSuccessTitle, setDropSuccessTitle] = useState(null)

  const submitDrop = ({ message, email, refundAmount, successTitle }) => {
    setActionLoading(true)
    dispatch(enrollmentActions.updateEnrollment({
      enrollmentData: { id: listEnrollment.id, status: 'dropped' }, coachMessage: message, notifyViaEmail: email, refundAmount,
    })).unwrap()
      .then(() => {
        closeModal()
        onActionComplete?.()
        loadEnrollment()
        setDropSuccessTitle(successTitle)
      })
      .catch(() => toast.error(t('enrollmentsBilling.actions.somethingWentWrong'), { position: toast.POSITION.TOP_RIGHT }))
      .finally(() => setActionLoading(false))
  }

  // Plain drop via the legacy modal (enrollments with no R3 invoice). A paid-online enrollment is
  // fully refunded server-side on drop, so don't send a refund_amount (the backend defaults to the
  // full captured amount) and reflect the refund in the success title.
  const handleDrop = () => {
    const paidOnline = enrollment?.paymentStatus === 'paid_online'
    const successTitle = paidOnline
      ? t('enrollmentsBilling.actions.enrollmentDroppedRefunded')
      : t('enrollmentsBilling.actions.enrollmentDropped')
    return submitDrop({ message: coachMessage, email: notifyViaEmail, successTitle })
  }

  // Legacy refund modal (paid-online, no R3 invoice) also lets the coach drop *without* refunding.
  // Send an explicit refund_amount of 0, since the backend otherwise refunds the full captured amount.
  const handleDropNoRefund = () => submitDrop({
    message: coachMessage,
    email: notifyViaEmail,
    refundAmount: 0,
    successTitle: t('enrollmentsBilling.actions.enrollmentDropped'),
  })

  // The Drop/Refund modal offers three choices: drop only, refund only, or both. Refund-only never
  // touches the enrollment status, so it goes to the invoice refund endpoint instead of the drop.
  const handleDropRefund = ({ notes, sendEmail, refundAmount, dropEnrollment = true, issueRefund = true }) => {
    if (!dropEnrollment) { return refundOnly?.({ refundAmount, notes, sendEmail }) }

    const amount = issueRefund ? refundAmount : 0
    return submitDrop({ message: notes, email: sendEmail, refundAmount: amount, successTitle: dropSuccessTitleFor(enrollment, amount, t) })
  }

  return { handleDrop, handleDropNoRefund, handleDropRefund, dropSuccessTitle, closeDropSuccess: () => setDropSuccessTitle(null) }
}

export default useDropActions
